import { DateTime } from 'luxon';

import { DefaultAccommodationOfferFragment } from '../../graphql/generated/graphql';
import { formatTripDate } from '../../helpers/formatDate';
import { getOutboundArrivalSegment } from '../flight/helpers/splitItineraryLegs';

type ComputeTripTitleProps = {
  flight?: {
    itineraries: {
      segments: {
        departure?: { datetime: string; city?: string; iataCode?: string };
        arrival?: { datetime: string; city?: string; iataCode?: string };
      }[];
    }[];
  };
  accommodation?: DefaultAccommodationOfferFragment | null;
  /** A rental car, the last resort for a car-only basket or trip: its pick up day and desk city */
  vehicle?: { pickUp: { dateTime: string; desk: { address: { cityName?: string | null } } } } | null;
};

/**
 * Word-month trip date, e.g. `17 April 2026`. The month is spelled out because an abbreviated one reads as a
 * truncation bug in a page header rather than as a deliberate short form.
 */
const TRIP_DATE_FORMAT = 'd LLLL yyyy';

/** A trip heading split into its two halves, so a header can separate them by weight and colour instead of a pipe. */
export type TripHeadingParts = {
  /** The emphasised half, e.g. `Trip to Melbourne`. Falls back to a bare `Trip` when no city resolves. */
  destination: string;
  /** The muted half, e.g. `17 April 2026`. Absent when neither the trip nor the offer resolves a date. */
  date?: string;
};

/** Backend-resolved trip fields, preferred over deriving the title from the offer in hand. */
type TripTitleOverride = {
  /** `Trip.destinationCity` — already turnaround-aware on the backend, so it beats any offer-derived city. */
  destinationCity?: string | null;
  /** `Trip.startDate` — the backend-resolved wall-clock day (`YYYY-MM-DD`), never the `startAt` instant. */
  startDate?: string | null;
};

/** A desk city as Sabre writes it (`MASCOT 2020`) made readable: postcode dropped, title case */
function deskCity(cityName: string | null | undefined): string | undefined {
  const words = (cityName ?? '').replace(/\d+/g, '').trim().toLowerCase();

  return words ? words.replace(/\b\p{L}/gu, (letter) => letter.toUpperCase()) : undefined;
}

export function computeArrivalCityAndDepartureDate({
  flight,
  accommodation,
  vehicle,
}: ComputeTripTitleProps): { city?: string; date: string } | undefined {
  if (flight) {
    const firstItinerary = flight.itineraries?.[0];
    const firstSegment = firstItinerary?.segments?.[0];
    // Use the outbound arrival, not the final arrival: a round trip can be a single itinerary that ends back at
    // the origin, where the last segment's arrival is the origin city rather than where the traveler flew to.
    const outboundArrival = getOutboundArrivalSegment(firstItinerary?.segments ?? []);

    // `setZone` keeps the airport's own offset: parsing in the viewer's zone re-dates an early-morning
    // departure to the previous day for anyone sitting west of it.
    const date = DateTime.fromISO(firstSegment?.departure?.datetime ?? '', { setZone: true }).toFormat(
      TRIP_DATE_FORMAT,
    );
    const arrivalCity = outboundArrival?.arrival?.city;

    return { city: arrivalCity, date };
  }

  if (accommodation) {
    const date = DateTime.fromISO(accommodation.checkIn.date, { setZone: true }).toFormat(TRIP_DATE_FORMAT);
    const arrivalCity = accommodation.address.cityName;

    return { city: arrivalCity, date };
  }

  if (vehicle) {
    const date = DateTime.fromISO(vehicle.pickUp.dateTime).toFormat(TRIP_DATE_FORMAT);

    return { city: deskCity(vehicle.pickUp.desk.address.cityName), date };
  }

  return undefined;
}

/**
 * Compute a trip heading as separate destination/date halves. Prefers the backend-resolved destination/start (which
 * already handles round trips modelled as a single itinerary), falling back to deriving them from the offer when no
 * trip override is supplied — which is the only option at checkout, where no trip exists yet.
 */
export function computeTripHeading(
  { flight, accommodation, vehicle }: ComputeTripTitleProps,
  override?: TripTitleOverride,
): TripHeadingParts {
  if (override?.destinationCity) {
    const date =
      formatTripDate(override.startDate, TRIP_DATE_FORMAT) ??
      computeArrivalCityAndDepartureDate({ flight, accommodation, vehicle })?.date;
    return { destination: `Trip to ${override.destinationCity}`, date };
  }

  const result = computeArrivalCityAndDepartureDate({ flight, accommodation, vehicle });

  if (!result) {
    return { destination: 'Trip' };
  }

  // A segment can carry an IATA code without a city name; naming the trip after nothing is worse than not naming it.
  return { destination: result.city ? `Trip to ${result.city}` : 'Trip', date: result.date };
}

/**
 * Flatten a heading into one string, for the places that cannot express the two halves through styling: the browser
 * tab title and the PDF itinerary.
 */
export function formatTripHeading({ destination, date }: TripHeadingParts): string {
  return date ? `${destination} | ${date}` : destination;
}

/** Compute the title of a trip as a single string. */
export function computeTripTitle(props: ComputeTripTitleProps, override?: TripTitleOverride): string {
  return formatTripHeading(computeTripHeading(props, override));
}
